import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, Box, Typography, Button, IconButton, Stack,
    Checkbox, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EXTENSION_STORE_URL } from '../utils/extensionBridge';

/**
 * The checkout interstitial for shoppers without the extension, patterned on
 * the two-column install modals users already know (image left, pitch and
 * CTA right). Shown on every checkout click until the extension is installed
 * or the user checks "Don't show this popup again"; continuing is always one
 * click and never blocked.
 *
 * The price-alert card on the left is a live HTML replica of the real card
 * the extension renders on retailer pages (same trick as the onboarding
 * page) — crisp at any pixel density, no screenshot assets.
 */

const CardReplica = () => (
    <Box
        sx={{
            width: '100%',
            maxWidth: 340,
            bgcolor: '#ffffff',
            color: '#16232e',
            borderRadius: '12px',
            borderLeft: '5px solid #0a9950',
            boxShadow: '0 10px 32px rgba(0, 0, 0, 0.22), 0 2px 8px rgba(0, 0, 0, 0.12)',
            p: '14px 16px',
            textAlign: 'left',
            fontSize: '13.5px',
            lineHeight: 1.5
        }}
    >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Box
                component="img"
                src="/logo192.png"
                alt=""
                sx={{ width: 18, height: 18, borderRadius: '50%' }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#16232e' }}>
                StockMaid Price Alert
            </Typography>
        </Stack>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#067d42' }}>
            Save $200.00
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', mb: 0.5 }}>
            Newegg has this for $1,699.99.
        </Typography>
        <Typography sx={{ fontSize: '12.5px', color: '#5a6773', mb: 1.25 }}>
            Price checked 1 min ago.
        </Typography>
        <Box
            sx={{
                display: 'inline-block',
                bgcolor: '#0a9950',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 13,
                px: '14px',
                py: '7px',
                borderRadius: '8px'
            }}
        >
            Buy at Newegg
        </Box>
    </Box>
);

export const ExtensionPromptDialog = ({ open, retailerName, onGetExtension, onContinue, onClose }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (open) {
            setDontShowAgain(false);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={() => onClose(dontShowAgain)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 520,
                    width: 'calc(100% - 16px)',
                    bgcolor: 'var(--background-color-light)',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundImage: 'none',
                    position: 'relative',
                    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
                }
            }}
        >
            <IconButton
                onClick={() => onClose(dontShowAgain)}
                aria-label="Close"
                disableRipple
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    color: 'text.primary',
                    bgcolor: 'var(--background-color-light)',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    p: 0,
                    '&:hover': { bgcolor: 'var(--background-color-dark)', color: 'text.primary' },
                    zIndex: 1
                }}
            >
                <CloseIcon sx={{ fontSize: 30 }} />
            </IconButton>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ textAlign: 'center', px: 4, pt: 6, pb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <CardReplica />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{ mb: 2, color: 'text.primary', lineHeight: 1.25, fontWeight: 700 }}
                    >
                        Never overpay. Never miss a restock.
                    </Typography>

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={dontShowAgain}
                                onChange={(event) => setDontShowAgain(event.target.checked)}
                                size="small"
                            />
                        )}
                        label={(
                            <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                                Don't show this popup again
                            </Typography>
                        )}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        component="a"
                        href={EXTENSION_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => onGetExtension(dontShowAgain)}
                        variant="contained"
                        fullWidth
                        sx={{
                            bgcolor: 'var(--sm-btn-contained-bg)',
                            color: 'var(--sm-btn-contained-text)',
                            py: 1.4,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="img"
                                src="/images/chrome-extensions-4312d461.png"
                                alt=""
                                sx={{ width: 22, height: 22, borderRadius: '6px' }}
                            />
                            Get the Chrome Extension
                        </Box>
                    </Button>

                    <Button
                        onClick={() => onContinue(dontShowAgain)}
                        fullWidth
                        sx={{
                            mt: 1,
                            color: 'text.primary',
                            textTransform: 'none',
                            fontWeight: 500,
                            textDecoration: 'underline',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {`Continue to ${retailerName || 'the retailer'}`}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ExtensionPromptDialog;
